import { Routes } from '@angular/router';

import { authGuard } from './core/auth/auth.guard';

// SSO-only flow (per ADR-0001): the app has no internal login page. Anonymous
// users hitting any protected route are redirected to the IdP by `authGuard`.
// `/auth` is the IdP return target — the APP_INITIALIZER exchanges the token
// before the router activates, then `AuthCallbackComponent` routes to the
// post-auth destination.
//
// Direct email/password login is intentionally not wired. If reintroduced
// later, it should be a separate flow under `/auth/...` with its own ADR.
export const routes: Routes = [
  {
    path: 'auth',
    loadComponent: () =>
      import('./features/auth/callback/auth-callback.component').then(
        (m) => m.AuthCallbackComponent,
      ),
  },
  {
    path: 'auth/callback',
    loadComponent: () =>
      import('./features/auth/callback/auth-callback.component').then(
        (m) => m.AuthCallbackComponent,
      ),
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./shared/layout/shell.component').then((m) => m.ShellComponent),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        // Landing page. Lazy feature routes (KPM2-152) — the shell composes
        // independently-loaded widget regions; heavy widgets defer their own
        // chunks so the initial bundle stays lean.
        path: 'dashboard',
        loadChildren: () =>
          import('./features/dashboard/dashboard.routes').then(
            (m) => m.DASHBOARD_ROUTES,
          ),
      },
      {
        // Dev harness for the dashboard widgets (KPM2-152). Not in navigation;
        // reachable at `/dev/dashboard` for backend-free visual review of the
        // shell, the widget-card states, and the @defer-loaded kpm-chart — with
        // mock data. `*-sandbox.component.ts` is coverage-excluded (angular.json).
        path: 'dev/dashboard',
        loadComponent: () =>
          import(
            './features/dashboard/dashboard-sandbox.component'
          ).then((m) => m.DashboardSandboxComponent),
      },
      {
        path: 'ui-sandbox',
        loadComponent: () =>
          import('./features/ui-sandbox/ui-sandbox.component').then(
            (m) => m.UiSandboxComponent,
          ),
      },
      {
        // Dev harness for the bespoke shared/ui/kanban board (KPM2-105). Not in
        // navigation; reachable at `/dev/kanban` for backend-free review of
        // drag-drop, keyboard moves (Alt+Arrow), swimlanes and the overdue
        // decoration. The live Tasks Board that consumes it lands with the
        // Phase 5a task data foundation (KPM2-104).
        path: 'dev/kanban',
        loadComponent: () =>
          import('./shared/ui/kanban/kanban-sandbox.component').then(
            (m) => m.KanbanSandboxComponent,
          ),
      },
      {
        // Dev harness for the bespoke shared/ui/gantt chart (KPM2-106). Not in
        // navigation; reachable at `/dev/gantt` for backend-free review of
        // phase grouping, bars + progress, FS/FF dependency arrows and the
        // week/month/year scales.
        path: 'dev/gantt',
        loadComponent: () =>
          import('./shared/ui/gantt/gantt-sandbox.component').then(
            (m) => m.GanttSandboxComponent,
          ),
      },
      {
        // Dev harness for the common DocumentDialog (KPM2-46). Not surfaced in
        // navigation; reachable at `/dev/document-dialog` for visual review.
        path: 'dev/document-dialog',
        loadComponent: () =>
          import(
            './features/settings/documents/dialogs/document-dialog-sandbox.component'
          ).then((m) => m.DocumentDialogSandboxComponent),
      },
      {
        // Dev harness for the shared kpm-document-viewer (KPM2-54). Not in
        // navigation; reachable at `/dev/document-viewer` for visual review
        // of every renderer (image, video, audio, pdf, text, csv, fallback).
        path: 'dev/document-viewer',
        loadComponent: () =>
          import(
            './shared/ui/document-viewer/document-viewer-sandbox.component'
          ).then((m) => m.DocumentViewerSandboxComponent),
      },
      {
        // Dev harness for the case Activity surface (KPM2-103). Not in
        // navigation; reachable at `/dev/case-activity` for visual review of
        // the timeline / filter bar / note composer / thread expansion / D2
        // Reply — with mock data, no login (the live surface is backend-gated).
        path: 'dev/case-activity',
        loadComponent: () =>
          import(
            './features/cases/activity/case-activity-sandbox.component'
          ).then((m) => m.CaseActivitySandboxComponent),
      },
      {
        // Dev harness for the config-driven Prospect tab (KPM2-263, ADR-0034).
        // Not in navigation; reachable at `/dev/prospect-tab` for backend-free
        // visual review of the Landlord Requirements + Notes edit form
        // (A-EC3 config), hydration, and Save — no login. Real data + the shell
        // header land when the prospect route mounts the shell (P2, KPM2-264).
        path: 'dev/prospect-tab',
        loadComponent: () =>
          import('./features/prospects/detail/prospect-tab-sandbox.component').then(
            (m) => m.ProspectTabSandboxComponent,
          ),
      },
      {
        // Path matches the legacy menu API's `page_url` value verbatim, so a
        // sidebar item with `page_url: "customsettings"` navigates here with
        // no menu-mapper translation. The internal folder is still
        // `features/settings/` — naming describes the feature, not the URL.
        path: 'customsettings',
        loadChildren: () =>
          import('./features/settings/settings.routes').then(
            (m) => m.SETTINGS_ROUTES,
          ),
      },
      {
        // Same verbatim-page_url rule as `customsettings`: the legacy menu
        // API returns `page_url: "accountsbilling"`, so this is the URL.
        // Internal folder is `features/subscription/` — naming follows the
        // feature, not the legacy URL. Single-line cosmetic rename point
        // lives in `subscription-sections.ts` (`SUBSCRIPTION_BASE_PATH`).
        // Migration breakup: epic KPM2-56, shell ticket KPM2-57.
        path: 'accountsbilling',
        loadChildren: () =>
          import('./features/subscription/subscription.routes').then(
            (m) => m.SUBSCRIPTION_ROUTES,
          ),
      },
      {
        // Legacy compatibility (KPM2-67): the old AngularJS app had a
        // top-level `/accountsbillingupgrade` UI-router state
        // (`app.js#L1817-1842`) — a 17-line stub with no real logic. The new
        // app never wired it; this redirect catches any stale bookmark or
        // external link and lands it on the plans page (the modern upgrade
        // entry point) instead of falling through the `**` wildcard to the
        // dashboard. Lives here (not in `subscription.routes.ts`) because the
        // legacy URL is top-level, not under `/accountsbilling`.
        path: 'accountsbillingupgrade',
        pathMatch: 'full',
        redirectTo: 'accountsbilling/plans',
      },
      {
        // Same verbatim-page_url rule as the two routes above: the legacy
        // menu API returns `page_url: "messages"`, so this is the URL.
        // `app.js#L284-L290` captures the legacy `/messages?id=<msg>`
        // deeplink form — the new shell canonicalises it to `/messages/:id`
        // (per ADR-0007). Migration breakup: epic KPM2-79, shell ticket
        // KPM2-81. `MESSAGES_BASE_PATH` in `shared/messages-base-path.ts`
        // is the single-line cosmetic rename point.
        path: 'messages',
        loadChildren: () =>
          import('./features/messages/messages.routes').then(
            (m) => m.MESSAGES_ROUTES,
          ),
      },
      {
        // Same verbatim-page_url rule as the routes above: the legacy menu API
        // returns `page_url: "cases"` (item `mn0008`), so this is the URL.
        // Internal folder is `features/cases/`. Migration breakup: epic
        // KPM2-97, parent story KPM2-98, shell ticket KPM2-100.
        // `CASES_BASE_PATH` in `shared/cases-base-path.ts` is the single-line
        // cosmetic rename point. The legacy `/cases?id=<id>` deeplink form
        // (`app.js#L267`) is canonicalised to `/cases/:caseNumber` by the
        // shell, like `/messages?id=`.
        path: 'cases',
        loadChildren: () =>
          import('./features/cases/cases.routes').then((m) => m.CASES_ROUTES),
      },
      {
        // Same verbatim-page_url rule as the routes above: the legacy menu API
        // returns `page_url: "properties"`, so this is the URL. Internal folder
        // is `features/properties/`. Migration breakup: epic KPM2-160, parent
        // story KPM2-161, routing ticket KPM2-163. `PROPERTIES_BASE_PATH` in
        // `features/properties/shared/properties-base-path.ts` is the
        // single-line cosmetic rename point. The legacy `/properties?id=<id>`
        // deeplink form (`app.js#L254`) is canonicalised to `/properties/:id`
        // by the shell, like `/cases?id=` (ADR-0016).
        path: 'properties',
        loadChildren: () =>
          import('./features/properties/properties.routes').then(
            (m) => m.PROPERTIES_ROUTES,
          ),
      },
      {
        // Same verbatim-page_url rule as the routes above: the legacy menu API
        // returns `page_url: "propertyfinancesummary"` (state
        // `app.propertyfinancedata`, `app.js#L3904-L3931`), so this is the URL.
        // Internal folder is `features/property-finance/` — the first slice of
        // the legacy `modules/propertyfinance/` module (ADR-0023). It is a
        // top-level sibling of `/properties`, not a child; its breadcrumb
        // declares a synthetic Properties parent so it still reads
        // `Home › Properties › Property Finance Data`. Migration breakup: epic
        // KPM2-201, routing ticket KPM2-203. `PROPERTY_FINANCE_SUMMARY_PATH` in
        // `features/property-finance/shared/property-finance-base-path.ts` is
        // the single-line cosmetic rename point. No legacy `?id=` deeplink
        // exists, so no canonicalising redirect is needed.
        path: 'propertyfinancesummary',
        loadChildren: () =>
          import('./features/property-finance/property-finance.routes').then(
            (m) => m.PROPERTY_FINANCE_ROUTES,
          ),
      },
      {
        // Stage B sibling (ADR-0024, epic KPM2-207): the multi-property
        // aggregate finance dashboard (legacy state
        // `app.propertyfinancedashboard`, `app.js#L3932-L3959`) — verbatim
        // page_url, same `features/property-finance/` folder and synthetic
        // Properties breadcrumb parent as the summary above. The sidebar
        // "Finance Dashboard" item reaches this via the
        // `MIGRATED_ROUTE_OVERRIDES` label bridge in `menu.mapper.ts` (its DB
        // `page_url` is a legacy slug). No legacy deeplink params.
        path: 'propertyfinancedashboard',
        loadChildren: () =>
          import('./features/property-finance/property-finance.routes').then(
            (m) => m.PROPERTY_FINANCE_DASHBOARD_ROUTES,
          ),
      },
      {
        // Standalone cross-property marketing grid (ADR-0025, epic KPM2-213).
        // Legacy state `app.propertymarketingdata` (`app.js#L3823`) — verbatim
        // page_url, so a legacy `/propertymarketingdata` bookmark resolves
        // unchanged (registering the route IS the deeplink fix). It is a
        // top-level sibling of `/properties`, NOT a `/properties/:id` child:
        // the grid is entity-wide and the legacy URL carries no id. Single-
        // component page → `loadComponent` (no shell). The `marketingedit`
        // drill into the un-migrated marketing-details editor is nav-gated
        // (ADR-0025 §2). Migration breakup: parent story KPM2-215.
        path: 'propertymarketingdata',
        loadComponent: () =>
          import(
            './features/property-marketing/property-marketing.component'
          ).then((m) => m.PropertyMarketingComponent),
        title: 'Property Marketing',
        data: { breadcrumb: 'Property Marketing' },
      },
      {
        // Standalone landlord grid (ADR-0027, epic KPM2-219). Legacy state
        // `app.landlords` (`app.js#L1918`) — verbatim page_url `/landlord`
        // (singular), so a legacy bookmark resolves unchanged and the
        // backend-menu item needs no mapper translation. Entity-wide list
        // (`uiGridParam $entity_id`) → top-level sibling, no `:id` in the URL.
        // Title/breadcrumb "Landlords" per the legacy breadcrumb registry
        // (`breadcrumb.js:210`); the on-page grid header stays "Landlord".
        // The legacy `/landlord?id=<id>` deeplink jumped into the details
        // editor (`app.js#L266-L274`) — un-migrated (own future epic), so it
        // lands here on the grid with `id` riding along (ADR-0027 §3a,
        // R-LLa); it canonicalises to `/landlord/:id` when the details epic
        // lands. Migration breakup: parent story KPM2-221.
        path: 'landlord',
        loadComponent: () =>
          import('./features/landlord/landlord.component').then(
            (m) => m.LandlordComponent,
          ),
        title: 'Landlords',
        data: { breadcrumb: 'Landlords' },
      },
      {
        // Legacy compatibility (ADR-0027 §3b): `/landlordDetails`
        // (`app.js#L1943`) was the landlord editor — un-migrated, and the
        // legacy URL carried no id (`globalVar` ferried it), so a stale
        // bookmark can only land on the list (never the `**` dashboard drop).
        path: 'landlordDetails',
        pathMatch: 'full',
        redirectTo: 'landlord',
      },
      {
        // Lease ("Freeholder") full-module tree (ADR-0028, epic KPM2-226).
        // Verbatim page_url `lease` (legacy state `app.lease`, `app.js#L3549`),
        // so a legacy `/lease` bookmark resolves unchanged and the backend-menu
        // item (`mn0044`, displayName "Freeholder") needs no mapper translation.
        // Internal folder is `features/lease/`; `LEASE_BASE_PATH` in
        // `features/lease/shared/lease-base-path.ts` is the single-line cosmetic
        // rename point. Detail is `/lease/:id` (raw leaseId PK, ADR-0016).
        // Migration breakup: P0 scaffold KPM2-227.
        path: 'lease',
        loadChildren: () =>
          import('./features/lease/lease.routes').then((m) => m.LEASE_ROUTES),
      },
      // Legacy compatibility (ADR-0028 §4): the lease module's other three
      // states + the commented-out lowercase `app.leasedetails` all carried
      // their context via `globalVar` (no usable URL id — `services.js`), so a
      // stale bookmark can only land on the list, never the `**` dashboard drop.
      // `/leaseDetails` (`app.js#L3576`), `/leasedetails` (dead lowercase,
      // `app.js#L2478`), `/leaseactivity` (`app.js#L3610`), `/addnewlease`
      // (`app.js#L2504`). They canonicalise to `/lease/:id` when the detail epic
      // lands (ADR-0028 / ADR-0019 `?id=` precedent).
      ...['leaseDetails', 'leasedetails', 'leaseactivity', 'addnewlease'].map(
        (legacyPath) => ({
          path: legacyPath,
          pathMatch: 'full' as const,
          redirectTo: 'lease',
        }),
      ),
      {
        // Supplier ("service provider") full-module tree (ADR-0031, epic
        // KPM2-243). Verbatim page_url `supplier` (legacy state `app.suppliers`,
        // URL `/supplier` — `app.js#L1979-1981`; the state NAME is plural but
        // the URL is singular), so a legacy `/supplier` bookmark resolves
        // unchanged and the backend-menu item needs no mapper translation.
        // Internal folder is `features/supplier/`; `SUPPLIER_BASE_PATH` in
        // `features/supplier/shared/supplier-base-path.ts` is the single-line
        // cosmetic rename point. Detail is `/supplier/:id` (raw supplierId PK,
        // ADR-0016) — the legacy `$sessionStorage` supplierId/rowData handoff is
        // retired. Migration breakup: P0 scaffold KPM2-244.
        path: 'supplier',
        loadChildren: () =>
          import('./features/supplier/supplier.routes').then(
            (m) => m.SUPPLIER_ROUTES,
          ),
      },
      {
        // Legacy compatibility (ADR-0031 §3b): `/supplierDetails`
        // (`app.js#L2006-2008`) was the supplier detail — the legacy URL carried
        // no id (`globalVar.setSupplierId` ferried it), so a stale bookmark can
        // only land on the list, never the `**` dashboard drop. Canonicalises to
        // `/supplier/:id` via the `/document?context=supplier` deeplink below.
        path: 'supplierDetails',
        pathMatch: 'full',
        redirectTo: 'supplier',
      },
      {
        // Contacts full-module tree (ADR-0061, epic KPM2-436). Verbatim legacy
        // list URL `contacts` (state `app.contacts` — `app.js#L3246-3248`), so
        // a legacy bookmark resolves unchanged; the deployed menu `page_url` is
        // uncaptured (A-CN2, KPM2-442) so the mapper carries label bridges.
        // Internal folder is `features/contacts/`; `CONTACTS_BASE_PATH` in
        // `features/contacts/shared/contacts-base-path.ts` is the single-line
        // cosmetic rename point. Detail is `/contacts/:id` (the raw
        // `rowData.entity._userid` the legacy detail read,
        // `contactdetails.ctrl.js:151`, ADR-0016); the legacy null-rowData
        // add mode is the routed `/contacts/new`. Migration breakup: P0
        // scaffold KPM2-437.
        path: 'contacts',
        loadChildren: () =>
          import('./features/contacts/contacts.routes').then(
            (m) => m.CONTACTS_ROUTES,
          ),
      },
      {
        // Legacy compatibility (ADR-0061 §3): `/contact` (`app.js#L3270-3272`)
        // was the contact detail — the legacy URL carried no id
        // (`globalVar.setRowData` ferried it), so a stale bookmark can only
        // land on the list, never the `**` dashboard drop.
        path: 'contact',
        pathMatch: 'full',
        redirectTo: 'contacts',
      },
      {
        // Tenancy (tenant / lettings side) full-module tree (ADR-0032, epic
        // KPM2-249). Verbatim page_url `tenancy` (legacy states `app.tenancy`
        // `/tenancy` + `app.propertytenancy` `/propertytenancy` —
        // `app.js#L2277-L2331`; menu ids `mn0003`/`mn0005`,
        // `controller.js#L505-L511`), so a legacy `/tenancy` bookmark resolves
        // unchanged and the backend-menu item needs no mapper translation.
        // Internal folder is `features/tenancy/`; `TENANCY_BASE_PATH` in
        // `features/tenancy/shared/tenancy-base-path.ts` is the single-line
        // cosmetic rename point. Detail is `/tenancy/:id` (raw tenancyId PK,
        // ADR-0016) — the legacy `$sessionStorage` rowData/tenancyId handoff
        // (`services.js:554/566/668`) is retired; the property-scoped list twin
        // collapses to `/tenancy?propertyId=`. Migration breakup: P0 scaffold
        // KPM2-250.
        path: 'tenancy',
        loadChildren: () =>
          import('./features/tenancy/tenancy.routes').then(
            (m) => m.TENANCY_ROUTES,
          ),
      },
      // Legacy compatibility (ADR-0032 §2): the tenant-side states carried their
      // context via `globalVar`/`$sessionStorage` (no usable URL id), so a stale
      // bookmark can only land on the list, never the `**` dashboard drop.
      // `/tenancydetails` (`app.js#L2332`), `/propertytenancy` (`app.js#L2305` —
      // the property-scoped list twin, propertyId unrecoverable from the URL),
      // `/tenancyactivity` (`app.js#L3400`). The carved-out tenancy-AGENT states
      // (`/tenancyagent`, `/propertytenancyagent`, `/tenancyagentdetails` —
      // `app.js#L2190/L2217/L2243`; supplier-direction, own follow-up epic) also
      // land on `/tenancy` for now, where the list nav-gates the agent surface
      // until that epic ships.
      ...[
        'tenancydetails',
        'propertytenancy',
        'tenancyactivity',
        'tenancyagent',
        'propertytenancyagent',
        'tenancyagentdetails',
      ].map((legacyPath) => ({
        path: legacyPath,
        pathMatch: 'full' as const,
        redirectTo: 'tenancy',
      })),
      {
        // Landlord Prospects full-module tree (ADR-0030, epic KPM2-234).
        // Verbatim DEPLOYED page_url `landlordprospects` — the frozen source
        // has no such string (its states are `app.prospects`/`app.prospect`,
        // `app.js#L3726-L3789`); the deployed menu renamed the entry, same
        // deployed-vs-frozen divergence as lease/"Freeholder" (live capture
        // A-PRa). Internal folder is `features/prospects/`;
        // `PROSPECTS_BASE_PATH` in
        // `features/prospects/shared/prospects-base-path.ts` is the
        // single-line cosmetic rename point. Detail is
        // `/landlordprospects/:id` (raw prospect_id PK, ADR-0016 — the legacy
        // URL never carried the id; it rode `$sessionStorage`).
        // Migration breakup: P0 scaffold KPM2-235.
        path: 'landlordprospects',
        loadChildren: () =>
          import('./features/prospects/prospects.routes').then(
            (m) => m.PROSPECTS_ROUTES,
          ),
      },
      {
        // Tenancy Prospects (ADR-0035, epic KPM2-266) — the DEPLOYED-ONLY
        // sibling flavor of `/landlordprospects`, same component tree with the
        // tenancy `PROSPECT_FEATURE_CONFIG` token. This RECLAIMS the legacy
        // `/prospects` slug from the KPM2-234 redirect bridge below (ADR-0030
        // §4 assumed every prospect surface was the landlord one — the tenancy
        // flavor disproved that). Deployed label/`page_url` capture pending
        // (A-TPa); `TENANCY_PROSPECTS_BASE_PATH` is the rename point.
        path: 'prospects',
        loadChildren: () =>
          import('./features/prospects/prospects.routes').then(
            (m) => m.TENANCY_PROSPECTS_ROUTES,
          ),
      },
      {
        // Legacy compatibility (ADR-0030 §4, re-anchored by ADR-0035): the
        // legacy `/prospect` detail state carried its context via
        // `globalVar`/`$sessionStorage` (no URL id in ANY form — it even used
        // `prospectId == 0` as the Add sentinel, `prospects.ctrl.js#L51-L66`),
        // so a stale bookmark can only land on a list, never the `**`
        // dashboard drop. It now lands on the closest-named list (`/prospects`
        // — the flavor that kept the legacy slug); `/landlordprospects`
        // bookmarks never existed under this path.
        path: 'prospect',
        pathMatch: 'full' as const,
        redirectTo: 'prospects',
      },
      {
        // Standalone Viewings list (ADR-0033, epic KPM2-257) — the last
        // route of the legacy prospect module (state `app.viewings`,
        // `app.js#L3960`; carved out of KPM2-234). Verbatim page_url
        // `/viewings`; lives inside `features/prospects/` (same domain, same
        // API + dialogs). The legacy notification deep-link `/viewings?id=`
        // (`app.js#L368-L374`) resolves here unchanged — the list consumes
        // the `id` param (edit dialog auto-open, ADR-0033 §2) instead of the
        // retired `globalVar.PopupViewingId` handoff. Migration breakup:
        // P0 scaffold KPM2-258.
        path: 'viewings',
        loadChildren: () =>
          import('./features/prospects/viewings/viewings.routes').then(
            (m) => m.VIEWINGS_ROUTES,
          ),
      },
      {
        // Standalone cross-case Tasks page (ADR-0036, epic KPM2-272). Legacy
        // state `app.tasks` at `/tasks` (`app.js#L4627-L4652`) — verbatim
        // state URL, so a legacy `/tasks` bookmark resolves unchanged. The
        // deployed sidebar item's `page_url` is UNCAPTURED (no hardcoded menu
        // item or `mn` code exists in frozen source), so the label bridge in
        // `menu.mapper.ts` ships proactively (A-LLa class) pending the P6
        // live capture (KPM2-279). The legacy `/tasks?caseid=&taskid=`
        // deep-link form NEVER rendered the tasks page (`app.js#L275-L283`
        // sent it to the case view with auto-open-task); the shell redirects
        // it to `/cases/:caseNumber/activity?task=` (`tasks-shell.component.ts`).
        // Internal folder is `features/tasks/`; `TASKS_BASE_PATH` in
        // `features/tasks/shared/tasks-base-path.ts` is the single-line
        // cosmetic rename point. Migration breakup: P0 scaffold KPM2-273.
        path: 'tasks',
        loadChildren: () =>
          import('./features/tasks/tasks.routes').then((m) => m.TASKS_ROUTES),
      },
      {
        // Case Templates (ADR-0037, epic KPM2-281). Legacy state
        // `app.casetemplates` at `/casetemplates` (`app.js#L4726-L4750`) —
        // verbatim state URL, so a legacy bookmark resolves unchanged and a
        // sidebar item with `page_url: "casetemplates"` navigates here with
        // no mapper translation. The deployed item's `page_url`/label/bucket
        // are UNCAPTURED (A-CT2, live sweep KPM2-286); the label bridge in
        // `menu.mapper.ts` ships proactively (A-LLa class). Editor rides
        // `/casetemplates/:caseId` (ADR-0021 route-not-dialog; param
        // rationale in ADR-0037). Internal folder `features/casetemplates/`;
        // `CASETEMPLATES_BASE_PATH` is the single-line rename point.
        // Migration breakup: P0 scaffold KPM2-282.
        path: 'casetemplates',
        loadChildren: () =>
          import('./features/casetemplates/casetemplates.routes').then(
            (m) => m.CASETEMPLATES_ROUTES,
          ),
      },
      {
        // Legacy compatibility (ADR-0037): the old app's template editor was
        // a SEPARATE top-level state `app.casetemplate` at `/casetemplate`
        // (singular, `app.js#L4751-L4786`) with NO id in the URL — identity
        // rode `$sessionStorage.selectedCaseId`, which the new app retires.
        // A stale `/casetemplate` bookmark therefore can't name its template;
        // land it on the list. Lives here (not in `casetemplates.routes.ts`)
        // because the legacy URL is top-level, like `/case` below.
        path: 'casetemplate',
        pathMatch: 'full',
        redirectTo: 'casetemplates',
      },
      {
        // Message Templates (ADR-0062, epic KPM2-443). Legacy state
        // `app.templates` at `/templates` (`app.js#L3331-L3355`) — verbatim
        // state URL, so a legacy bookmark resolves unchanged and a sidebar
        // item with `page_url: "templates"` navigates here with no mapper
        // translation. NO label bridge exists or may ever exist for bare
        // 'Templates' — the ADR-0037 pre-commitment (`menu.mapper.ts`
        // doc): the label may be byte-identical to Case Templates', so only
        // page_url auto-resolution is structurally safe. The deployed item's
        // `page_url`/label/BUCKET are UNCAPTURED (A-TM2, live sweep
        // KPM2-450). Editor rides `/templates/new` (license-guarded) +
        // `/templates/:templateId` from P2 (ADR-0021 route-not-dialog).
        // Internal folder `features/templates/`. Migration breakup: P0
        // scaffold KPM2-444.
        path: 'templates',
        loadChildren: () =>
          import('./features/templates/templates.routes').then(
            (m) => m.TEMPLATES_ROUTES,
          ),
      },
      {
        // Legacy compatibility (ADR-0062): the old app's template editor was
        // a SEPARATE top-level state `app.template` at `/template`
        // (singular, `app.js#L3356-L3376`) with NO id in the URL — identity
        // rode `globalVar.setRowData`, which the new app retires. A stale
        // `/template` bookmark therefore can't name its template; land it on
        // the list (the `/casetemplate` precedent above).
        path: 'template',
        pathMatch: 'full',
        redirectTo: 'templates',
      },
      {
        // Standalone Recurring Case list (ADR-0038, epic KPM2-287). Legacy
        // state `app.recurringcase` at `/recurringcase` (`app.js#L5020-L5047`)
        // — verbatim state URL, so a legacy bookmark resolves unchanged and a
        // sidebar item with `page_url: "recurringcase"` navigates here with
        // no mapper translation. The deployed item's `page_url`/label/bucket
        // are UNCAPTURED (A-RC2, live sweep KPM2-291); the label bridge in
        // `menu.mapper.ts` ships proactively (A-LLa class). One grid page
        // (`pg0103`/`gd0114` — frozen-source ids, A-RC1); edit/history ride
        // the shared maintenance dialogs. Lives in `features/cases/` (Wave 6E
        // cases-family grouping). Migration breakup: P0 scaffold KPM2-288.
        path: 'recurringcase',
        loadChildren: () =>
          import('./features/cases/recurringcase/recurringcase.routes').then(
            (m) => m.RECURRINGCASE_ROUTES,
          ),
      },
      {
        // Customer Financials (ADR-0039, epic KPM2-294). Legacy state
        // `app.customerfinancial` at `/financials/customers`
        // (`app.js#L4072-L4098`) — verbatim state URL (the app's first
        // two-segment feature path; routing and the shell's prefix
        // active-match are segment-agnostic), so a legacy bookmark resolves
        // unchanged. The sidebar item's `page_url` is `customerfinancial`
        // (the STATE suffix — legacy `preClickFunc` did
        // `$state.go('app.' + page_url)`, `controller.js#L795-L803`), which
        // the mapper normalises to `/customerfinancial` — caught by the
        // redirect below. Deployed label/page_url capture rides A-CF1
        // (KPM2-301); the label bridge in `menu.mapper.ts` ships proactively
        // (A-LLa class). Internal folder `features/financials/customers/`;
        // `CUSTOMER_FINANCIALS_BASE_PATH` is the single-line rename point.
        // Migration breakup: P0 scaffold KPM2-295.
        path: 'financials/customers',
        loadChildren: () =>
          import('./features/financials/customers/customer-financials.routes').then(
            (m) => m.CUSTOMER_FINANCIALS_ROUTES,
          ),
      },
      {
        // Legacy compatibility (ADR-0039 §1): the backend menu item for the
        // page above carries `page_url: "customerfinancial"` — the mapper's
        // verbatim form would navigate to `/customerfinancial`, which is NOT
        // the page's URL. Catch it (and any stale external link of that
        // shape) instead of letting it fall through `**` to the dashboard.
        path: 'customerfinancial',
        pathMatch: 'full',
        redirectTo: 'financials/customers',
      },
      {
        // Consolidated Invoices (ADR-0056, epic KPM2-406). Legacy state
        // `app.consolidatedinvoices` at `/consolidatedfinancials/customers`
        // (`app.js#L4101-L4127`) — verbatim state URL, so a legacy bookmark
        // resolves unchanged. A stale fork of the customers page above whose
        // only rendered deltas are the grid id (`gd0112`,
        // `consolidatedinvoice.ctrl.js#L66`) and the H2 ("Consolidated
        // Invoices"). ⚠ The state displayName is "Customer Financials" —
        // byte-identical to the sibling above (`app.js#L4102`); the deployed
        // sidebar disambiguation is capture-gated A-CIN1 (KPM2-409). The
        // sidebar item's `page_url` is `consolidatedinvoices` (the STATE
        // suffix — same trap as `customerfinancial` above), caught by the
        // redirect below. Internal folder
        // `features/financials/consolidatedinvoices/`;
        // `CONSOLIDATED_INVOICES_BASE_PATH` is the single-line rename point.
        // Migration breakup: P0 scaffold KPM2-407.
        path: 'consolidatedfinancials/customers',
        loadChildren: () =>
          import('./features/financials/consolidatedinvoices/consolidated-invoices.routes').then(
            (m) => m.CONSOLIDATED_INVOICES_ROUTES,
          ),
      },
      {
        // Legacy compatibility (ADR-0056 §1): the backend menu item for the
        // page above carries `page_url: "consolidatedinvoices"` — the
        // mapper's verbatim form would navigate to `/consolidatedinvoices`,
        // which is NOT the page's URL. Catch it (and any stale external link
        // of that shape) instead of letting it fall through `**` to the
        // dashboard.
        path: 'consolidatedinvoices',
        pathMatch: 'full',
        redirectTo: 'consolidatedfinancials/customers',
      },
      {
        // Agency Bills (ADR-0041, epic KPM2-313). Legacy state
        // `app.agencyfinancial` at `/financials/agencybills`
        // (`app.js#L4239-L4265`) — verbatim state URL, so a legacy bookmark
        // resolves unchanged. A copy-paste fork of the customers page whose
        // only functional delta is the grid id (`gd0111`,
        // `agencyfinancial.ctrl.js#L84`). The sidebar item's `page_url` is
        // `agencyfinancial` (the STATE suffix — same trap as
        // `customerfinancial` above), caught by the redirect below. Deployed
        // label/page_url capture rides A-AB1 (KPM2-317); the label bridge in
        // `menu.mapper.ts` ships proactively (A-LLa class). Internal folder
        // `features/financials/agencybills/` (third ADR-0039 §2 family
        // member); `AGENCY_BILLS_BASE_PATH` is the single-line rename point.
        // Migration breakup: P0 scaffold KPM2-314.
        path: 'financials/agencybills',
        loadChildren: () =>
          import('./features/financials/agencybills/agency-bills.routes').then(
            (m) => m.AGENCY_BILLS_ROUTES,
          ),
      },
      {
        // Legacy compatibility (ADR-0041 §1, the ADR-0039 §1 class): the
        // backend menu item for the page above carries
        // `page_url: "agencyfinancial"` — the mapper's verbatim form would
        // navigate to `/agencyfinancial`, which is NOT the page's URL.
        path: 'agencyfinancial',
        pathMatch: 'full',
        redirectTo: 'financials/agencybills',
      },
      {
        // Landlord Statements (ADR-0043, epic KPM2-323). Legacy state
        // `app.landlordstatements` at `/landlordstatements`
        // (`app.js#L3673-L3696`) — verbatim state URL, so a legacy bookmark
        // resolves unchanged and a sidebar item with
        // `page_url: "landlordstatements"` navigates here with no mapper
        // translation. The deployed item's `page_url`/label/bucket are
        // UNCAPTURED (A-LS2, live sweep KPM2-331); the label bridge in
        // `menu.mapper.ts` ships proactively (A-LLa class) — landlord-
        // qualified forms ONLY (bare 'statements' would collide with the
        // un-migrated Tenant/Leaseholder/Consolidated statement items).
        // Workbench rides `/landlordstatements/:landlordId` (raw landlord
        // PK, ADR-0016) + `?statementId=` (`0` = live working document).
        // Internal folder `features/statements/landlord/` (first flavor of
        // the statements family, ADR-0043 §2);
        // `LANDLORD_STATEMENTS_BASE_PATH` is the single-line rename point.
        // Migration breakup: P0 scaffold KPM2-324.
        path: 'landlordstatements',
        loadChildren: () =>
          import(
            './features/statements/landlord/landlord-statements.routes'
          ).then((m) => m.LANDLORD_STATEMENTS_ROUTES),
      },
      {
        // Legacy compatibility (ADR-0043 §1): `/landlordstatement`
        // (singular, `app.js#L3697-L3723`) was the statement workbench — its
        // URL carried NO id (`globalVar` ferried the grid rowData / landlord
        // name / statement id, `landlordstatements.ctrl.js#L21-L25`), so a
        // stale bookmark can only land on the list, never the `**` dashboard
        // drop. It canonicalises to `/landlordstatements/:landlordId` only
        // via the list (the id is unrecoverable from the legacy URL).
        path: 'landlordstatement',
        pathMatch: 'full',
        redirectTo: 'landlordstatements',
      },
      {
        // Tenant Statement (ADR-0050, epic KPM2-365). Legacy state
        // `app.tenantstatement` at `/tenantstatement` (`app.js#L3727-L3755`)
        // — verbatim state URL, so a legacy bookmark resolves unchanged and
        // the sidebar item's `page_url` (FORCED to `tenantstatement` by the
        // state name) navigates here with no mapper translation. NO redirect
        // sibling (single legacy URL — unlike the landlord pair above). The
        // deployed item's label/bucket are UNCAPTURED (A-TS1, live sweep
        // KPM2-371); the label bridge in `menu.mapper.ts` ships proactively
        // (A-LLa class) — tenant/tenancy-qualified forms ONLY (bare
        // 'statement(s)' would collide with the un-migrated
        // Leaseholder/Consolidated statement items). The page IS the
        // workbench (no list sibling; the party is picked in-page) +
        // `?statementId=` (`0` = live working document). Internal folder
        // `features/statements/tenant/` (SECOND flavor of the statements
        // family — fires the ADR-0043 §2 promotion to `statements/shared/`,
        // KPM2-367); `TENANT_STATEMENT_BASE_PATH` is the single-line rename
        // point. Migration breakup: P0 scaffold KPM2-366.
        path: 'tenantstatement',
        loadChildren: () =>
          import('./features/statements/tenant/tenant-statement.routes').then(
            (m) => m.TENANT_STATEMENT_ROUTES,
          ),
      },
      {
        // Supplier Financials (ADR-0042, epic KPM2-318). Legacy state
        // `app.supplierfinancial` at `/financials/supplier`
        // (`app.js#L4126-L4152`) — verbatim state URL, so a legacy bookmark
        // resolves unchanged. The ORIGINAL the agencybills page byte-forked;
        // its grid identity is `gd0052` (`supplierfinancial.ctrl.js#L84`).
        // The sidebar item's `page_url` is `supplierfinancial` (the STATE
        // suffix — same trap as `customerfinancial` above), caught by the
        // redirect below. Deployed label/page_url capture rides A-SF1
        // (KPM2-322); the label bridge in `menu.mapper.ts` ships proactively
        // (A-LLa class) and is QUALIFIED only — bare `supplier(s)` belongs
        // to the `/supplier` entity module (ADR-0031). Internal folder
        // `features/financials/supplier/` (fourth ADR-0039 §2 family
        // member); `SUPPLIER_FINANCIALS_BASE_PATH` is the single-line
        // rename point. Migration breakup: P0 scaffold KPM2-319.
        path: 'financials/supplier',
        loadChildren: () =>
          import(
            './features/financials/supplier/supplier-financials.routes'
          ).then((m) => m.SUPPLIER_FINANCIALS_ROUTES),
      },
      {
        // Legacy compatibility (ADR-0042 §1, the ADR-0039 §1 class): the
        // backend menu item for the page above carries
        // `page_url: "supplierfinancial"` — the mapper's verbatim form would
        // navigate to `/supplierfinancial`, which is NOT the page's URL.
        path: 'supplierfinancial',
        pathMatch: 'full',
        redirectTo: 'financials/supplier',
      },
      {
        // Purchase Orders (ADR-0044, epic KPM2-332). Legacy state
        // `app.purchaseorders` at `/financials/purchaseorders`
        // (`app.js#L4180-L4206`) — verbatim state URL, so a legacy bookmark
        // resolves unchanged. Same fork lineage as the pg0024 family but its
        // OWN grid identity (`pg0094`/`gd0105`,
        // `purchaseorders.ctrl.js#L25-L26` — frozen-source, A-PO1). The
        // sidebar item's `page_url` is expected to be `purchaseorders` (the
        // STATE suffix — same trap as `customerfinancial` above), caught by
        // the redirect below. Deployed label/page_url capture rides A-PO2
        // (KPM2-336); the label bridge in `menu.mapper.ts` ships proactively
        // (A-LLa class). The PO EDITOR (`app.purchaseorder` at
        // `/purchaseorder`) is the ADR-0071 lazy route in the editor block
        // below (epic KPM2-501).
        // Internal folder `features/financials/purchaseorders/` (fifth
        // ADR-0039 §2 family member); `PURCHASE_ORDERS_BASE_PATH` is the
        // single-line rename point. Migration breakup: P0 scaffold KPM2-333.
        path: 'financials/purchaseorders',
        loadChildren: () =>
          import(
            './features/financials/purchaseorders/purchase-orders.routes'
          ).then((m) => m.PURCHASE_ORDERS_ROUTES),
      },
      {
        // Legacy compatibility (ADR-0044 §1, the ADR-0039 §1 class): the
        // backend menu item for the page above is expected to carry
        // `page_url: "purchaseorders"` — the mapper's verbatim form would
        // navigate to `/purchaseorders`, which is NOT the page's URL.
        path: 'purchaseorders',
        pathMatch: 'full',
        redirectTo: 'financials/purchaseorders',
      },
      {
        // Leaseholder Financials (ADR-0045, epic KPM2-337). Legacy state
        // `app.leaseholderfinancial` at `/financials/leaseholder`
        // (`app.js#L4153-L4179`) — verbatim state URL, so a legacy bookmark
        // resolves unchanged. A 22-line byte-fork of the supplier page whose
        // only functional delta is the grid id (`gd0116`,
        // `leaseholderfinancial.ctrl.js#L84`) — the pg0024-family residue
        // ADR-0044 pre-named "one-flavor-config-each". The sidebar item's
        // `page_url` is expected to be `leaseholderfinancial` (the STATE
        // suffix — same trap as `customerfinancial` above), caught by the
        // redirect below. Deployed label/page_url capture rides A-LF1/A-LF2
        // (KPM2-340 — the statements siblings drifted to "Freeholder"); the
        // label bridge in `menu.mapper.ts` ships proactively (A-LLa class)
        // and is QUALIFIED only. Internal folder
        // `features/financials/leaseholder/` (sixth ADR-0039 §2 family
        // member); `LEASEHOLDER_FINANCIALS_BASE_PATH` is the single-line
        // rename point. Migration breakup: P0 scaffold KPM2-338.
        path: 'financials/leaseholder',
        loadChildren: () =>
          import(
            './features/financials/leaseholder/leaseholder-financials.routes'
          ).then((m) => m.LEASEHOLDER_FINANCIALS_ROUTES),
      },
      {
        // Legacy compatibility (ADR-0045 §1, the ADR-0039 §1 class): the
        // backend menu item for the page above is expected to carry
        // `page_url: "leaseholderfinancial"` — the mapper's verbatim form
        // would navigate to `/leaseholderfinancial`, which is NOT the page's
        // URL.
        path: 'leaseholderfinancial',
        pathMatch: 'full',
        redirectTo: 'financials/leaseholder',
      },
      {
        // Bank Accounts (ADR-0046, epic KPM2-341). Legacy state
        // `app.financialbankaccounts` at `/financials/bankaccounts`
        // (`app.js#L4347-L4370`) — verbatim state URL, so a legacy bookmark
        // resolves unchanged. A BESPOKE family (statements-workbench shape,
        // ADR-0043 §2 class), NOT a flavor of the ADR-0041 shared
        // transactions host — the module has NO pg/gd config grids at all;
        // every surface is a custom table/card set over bespoke endpoints.
        // Workbench rides `/financials/bankaccounts/:accountId` (raw account
        // PK, ADR-0016) + `?accountName=` breadcrumb hand-off. The sidebar
        // item's `page_url` is expected to be `financialbankaccounts` (the
        // STATE suffix — same trap as `customerfinancial` above), caught by
        // the first redirect below. Deployed label/page_url capture rides
        // A-BA1 (KPM2-349); the label bridge in `menu.mapper.ts` ships
        // proactively (A-LLa class) and is QUALIFIED only — bare
        // `bank`/`banking` are never bridged, and `bank feeds` belongs to
        // the un-migrated `financialbankfeeds` module. Internal folder
        // `features/financials/bankaccounts/` (seventh ADR-0039 §2 family
        // member); `BANK_ACCOUNTS_BASE_PATH` is the single-line rename
        // point. Migration breakup: P0 scaffold KPM2-342.
        path: 'financials/bankaccounts',
        loadChildren: () =>
          import(
            './features/financials/bankaccounts/bank-accounts.routes'
          ).then((m) => m.BANK_ACCOUNTS_ROUTES),
      },
      {
        // Legacy compatibility (ADR-0046 §1, the ADR-0039 §1 class): the
        // backend menu item for the page above is expected to carry
        // `page_url: "financialbankaccounts"` — the mapper's verbatim form
        // would navigate to `/financialbankaccounts`, which is NOT the
        // page's URL.
        path: 'financialbankaccounts',
        pathMatch: 'full',
        redirectTo: 'financials/bankaccounts',
      },
      {
        // Legacy compatibility (ADR-0046 §1): `/financials/bankaccount`
        // (singular, `app.js#L4599-L4622`) was the account workbench — its
        // URL carried NO id (`globalVar` ferried account id + name,
        // `breadcrumb.js#L594-L598`), so a stale bookmark can only land on
        // the list, never the `**` dashboard drop. It canonicalises to
        // `/financials/bankaccounts/:accountId` only via the list (the id is
        // unrecoverable from the legacy URL) — the `/landlordstatement`
        // precedent (ADR-0043 §1).
        path: 'financials/bankaccount',
        pathMatch: 'full',
        redirectTo: 'financials/bankaccounts',
      },
      {
        // Financial Transactions (ADR-0047, epic KPM2-350). Legacy state
        // `app.financialtransaction` at `/financials/transactions`
        // (`app.js#L4266-L4291`) — verbatim state URL, so a legacy bookmark
        // resolves unchanged. This is the pg0024 FAMILY HUB (the
        // "Transactions" landing over `gd0092`,
        // `financialtransaction.ctrl.js#L17-L18`), NOT a per-party byte-fork:
        // its own TRANSACTION catalogue, `_expensetype` row typing, and — the
        // only family controller so — no tenant grid swap (ADR-0047 §3/§4).
        // The sidebar item's `page_url` is expected to be
        // `financialtransaction` (the STATE suffix — same trap as
        // `customerfinancial` above), caught by the redirect below. Deployed
        // label/page_url capture rides A-FT2 (KPM2-354); the menu bridge in
        // `menu.mapper.ts` ships proactively (A-LLa class) and is QUALIFIED
        // only — bare "Transactions" collides with the legacy top-level
        // `/transactions` module (`app.transactions`, identical label) and is
        // never bridged. The four transaction editors
        // (expense/deposit/journal/transfer) are a separate follow-up epic
        // (ADR-0047 §6). Internal folder `features/financials/transactions/`
        // (seventh ADR-0039 §2 family member);
        // `FINANCIAL_TRANSACTIONS_BASE_PATH` is the single-line rename point.
        // Migration breakup: P0 scaffold KPM2-351.
        path: 'financials/transactions',
        loadChildren: () =>
          import(
            './features/financials/transactions/financial-transactions.routes'
          ).then((m) => m.FINANCIAL_TRANSACTIONS_ROUTES),
      },
      {
        // Legacy compatibility (ADR-0047 §1, the ADR-0039 §1 class): the
        // backend menu item for the page above is expected to carry
        // `page_url: "financialtransaction"` — the mapper's verbatim form
        // would navigate to `/financialtransaction`, which is NOT the page's
        // URL.
        path: 'financialtransaction',
        pathMatch: 'full',
        redirectTo: 'financials/transactions',
      },
      {
        // Template Transactions (ADR-0048, epic KPM2-355). Legacy state
        // `app.templatefinancial` at `/financials/templates`
        // (`app.js#L4293-L4319`) — verbatim state URL, so a legacy bookmark
        // resolves unchanged. The recurring-transaction TEMPLATES list
        // (H2 "Recurring Transaction" over `gd0108`,
        // `financialtemplate.ctrl.js#L16-L21`) — a byte-clone of the
        // financialtransaction hub controller except grid identity, an 8-way
        // dual-type-key row-open dispatch, and two suppressed surfaces
        // (Add-New commented out of the deployed view; attach-case never
        // dispatchable — ADR-0048 §3/§4). The sidebar item's `page_url` is
        // expected to be `templatefinancial` (the STATE suffix — same trap
        // as `financialtransaction` above), caught by the redirect below.
        // Deployed label/page_url capture rides A-TF2 (KPM2-359); the menu
        // bridge in `menu.mapper.ts` ships proactively (A-LLa class) and is
        // QUALIFIED only — bare "Templates" collides with `/casetemplates`
        // (ADR-0037) and bare "Transactions" with the legacy top-level
        // `/transactions` module (ADR-0047); neither is ever bridged. The 8
        // row-open editor destinations are separate follow-up epics
        // (ADR-0048 §6). Internal folder `features/financials/templates/`
        // (eighth ADR-0039 §2 family member);
        // `FINANCIAL_TEMPLATES_BASE_PATH` is the single-line rename point.
        // Migration breakup: P0 scaffold KPM2-356.
        path: 'financials/templates',
        loadChildren: () =>
          import(
            './features/financials/templates/financial-templates.routes'
          ).then((m) => m.FINANCIAL_TEMPLATES_ROUTES),
      },
      {
        // Legacy compatibility (ADR-0048 §1, the ADR-0039 §1 class): the
        // backend menu item for the page above is expected to carry
        // `page_url: "templatefinancial"` — the mapper's verbatim form
        // would navigate to `/templatefinancial`, which is NOT the page's
        // URL.
        path: 'templatefinancial',
        pathMatch: 'full',
        redirectTo: 'financials/templates',
      },
      {
        // Bank Balance Report (ADR-0049, epic KPM2-360). Legacy state
        // `app.bankreports` at `/bankreports` (`app.js#L4863-L4887`) — a
        // BESPOKE read-only report page (4-filter bar + hand-rolled table +
        // Excel export), NOT a pg/gd config grid and NOT a
        // TRANSACTIONS_PAGE_CONFIG flavor (the ADR-0043 bespoke class): the
        // first financials page with no grid-id capture gate. ~75-80% of the
        // legacy controller is dead tasks-module residue that does NOT
        // migrate (ADR-0049 §5c). The sidebar `page_url` is forced to be
        // `bankreports` by the state name, caught by the redirect below;
        // deployed label/menu capture rides A-BR1 (KPM2-364). The menu
        // bridge in `menu.mapper.ts` ships proactively (A-LLa class),
        // QUALIFIED only — bare 'reports'/'financials' are never bridged.
        // Zero row actions/Add-New → zero nav-gates. Internal folder
        // `features/financials/bankreports/`; `BANK_REPORTS_BASE_PATH` is
        // the single-line rename point. Migration breakup: P0 scaffold
        // KPM2-361.
        path: 'financials/bankreports',
        loadChildren: () =>
          import('./features/financials/bankreports/bank-reports.routes').then(
            (m) => m.BANK_REPORTS_ROUTES,
          ),
      },
      {
        // Legacy compatibility (ADR-0049 §1, the ADR-0039 §1 class): legacy
        // bookmarks hit `/bankreports` verbatim, and the backend menu item
        // is expected to carry `page_url: "bankreports"` — the mapper's
        // verbatim form would navigate to `/bankreports`, which is NOT the
        // page's URL.
        path: 'bankreports',
        pathMatch: 'full',
        redirectTo: 'financials/bankreports',
      },
      {
        // Freeholder ("Leaseholder") Statements (ADR-0051, epic KPM2-372).
        // Legacy state `app.leaseholderstatements` at `/leaseholderstatements`
        // (`app.js#L4653-L4677`) — verbatim state URL, so a legacy bookmark
        // resolves unchanged and a sidebar item with
        // `page_url: "leaseholderstatements"` navigates here with no mapper
        // translation. NOT a pg/gd grid page — the FIRST config-driven TABLE
        // page: `fetchentityconfdetail` GUIDs are backend-owned and
        // capture-gated exactly like grid ids (A-LST2, live sweep KPM2-378).
        // The deployed item's label/bucket are UNCAPTURED (A-LST1); the label
        // bridge in `menu.mapper.ts` ships proactively (A-LLa class) —
        // freeholder/leaseholder-qualified forms ONLY (bare 'statement(s)'
        // would collide with the un-migrated Consolidated sibling; the
        // deployed breadcrumb registry reads "Freeholder Statements",
        // `breadcrumb.js#L668-L671`). History rides
        // `/leaseholderstatements/history` (no params — the legacy history
        // URL carried no id, see the redirect below). Internal folder
        // `features/statements/leaseholder/` (third flavor of the statements
        // family, ADR-0043 §2; the `statements/shared` promotion rides the
        // concurrent tenant epic, ADR-0050 §3 / KPM2-367);
        // `LEASEHOLDER_STATEMENTS_BASE_PATH` is the single-line rename point.
        // Migration breakup: P0 scaffold KPM2-373.
        path: 'leaseholderstatements',
        loadChildren: () =>
          import(
            './features/statements/leaseholder/leaseholder-statements.routes'
          ).then((m) => m.LEASEHOLDER_STATEMENTS_ROUTES),
      },
      {
        // Legacy compatibility (ADR-0051 §1, the ADR-0039 §1 class):
        // `/leaseholderstatementhistory` (`app.js#L4678-L4702`) was the
        // statement-history page — a 3-line byte-fork of the list whose only
        // functional delta is the config GUID. Its URL carried NO id (the
        // `viewHistory` sessionStorage hand-off is written but never read —
        // dead code, ADR-0051 §1), so this redirect is lossless: the history
        // page loads its full unfiltered table either way.
        path: 'leaseholderstatementhistory',
        pathMatch: 'full',
        redirectTo: 'leaseholderstatements/history',
      },
      {
        // Consolidated Statements (ADR-0057, epic KPM2-410). Legacy state
        // `app.consolidatedstatements` at `/consolidatedstatements`
        // (`app.js#L4917-L4940`) — verbatim state URL, so a legacy bookmark
        // resolves unchanged and a sidebar item with
        // `page_url: "consolidatedstatements"` navigates here with no mapper
        // translation. NOT a pg/gd grid page (hand-rolled tables, the
        // ADR-0049 no-grid-id class). The deployed item's label/bucket are
        // UNCAPTURED (A-CS1, live sweep KPM2-416); the label bridge in
        // `menu.mapper.ts` ships proactively (A-LLa class) —
        // consolidated-statement-qualified forms ONLY (bare 'statement(s)'
        // would collide with the sibling statement items and bare
        // 'consolidated' is shared with the un-migrated
        // `/consolidatedparties`, KPM2-399). The workbench rides
        // `:statementId` (`0` = new, the raw-PK class ADR-0016), replacing
        // the legacy `globalVar.setConsdStatementId/Name` ferry. Internal
        // folder `features/statements/consolidated/` (FOURTH flavor of the
        // statements family, ADR-0043 §2);
        // `CONSOLIDATED_STATEMENTS_BASE_PATH` is the single-line rename
        // point. Migration breakup: P0 scaffold KPM2-411.
        path: 'consolidatedstatements',
        loadChildren: () =>
          import(
            './features/statements/consolidated/consolidated-statements.routes'
          ).then((m) => m.CONSOLIDATED_STATEMENTS_ROUTES),
      },
      {
        // Legacy compatibility (ADR-0057 §1, the ADR-0039 §1 class):
        // `/consolidatedstatement` (singular, `app.js#L4941-L4965`) was the
        // statement workbench — its URL carried NO id (the
        // `globalVar.setConsdStatementId/Name` ferry held it in memory,
        // `consolidatedstatements.ctrl.js#L88-L104`), so this redirect is
        // lossless: a stale bookmark can only land on the list, never the
        // `**` dashboard drop. It canonicalises to
        // `/consolidatedstatements/:statementId` only via the list (the id
        // is unrecoverable from the legacy URL; the `/landlordstatement`
        // precedent, ADR-0043 §1).
        path: 'consolidatedstatement',
        pathMatch: 'full',
        redirectTo: 'consolidatedstatements',
      },
      {
        // Notifications (ADR-0052, epic KPM2-379). Legacy state
        // `app.notification` at `/notification` (`app.js#L2427-L2453`,
        // singular) — verbatim state URL, so legacy bookmarks resolve
        // unchanged and NO legacy-compat redirect is needed. NOT a sidebar
        // item — the deployed entry point is the topbar bell's "View all"
        // footer (`templates/menu.html#L416`), so there is NO menu bridge
        // either; breadcrumb registry reads "Notification" with parent Home
        // (`breadcrumb.js#L133-L136`). The bell itself is shell chrome
        // (`features/notifications/bell/`, mounted in the shell topbar —
        // ADR-0052 §3), not a route. Internal folder
        // `features/notifications/`; `NOTIFICATIONS_BASE_PATH` is the
        // single-line rename point. Migration breakup: P0 scaffold KPM2-380.
        path: 'notification',
        loadChildren: () =>
          import('./features/notifications/notifications.routes').then(
            (m) => m.NOTIFICATIONS_ROUTES,
          ),
      },
      {
        // Message Board (ADR-0074, epic KPM2-531). Legacy state
        // `app.messageboard` at `/messageboard` (`app.js#L3551-L3576`) —
        // verbatim state URL, so legacy bookmarks resolve unchanged and NO
        // legacy-compat redirect is needed. Until KPM2-532 this path fell
        // through the `**` wildcard below to `/dashboard`: a SILENT dead
        // link, which is also why the `/properties` message-board sidebar
        // (KPM2-164) has been sending "View All" to `/messages` — the
        // mailbox — as an ADR-0016 live-target stand-in. P5 (KPM2-538)
        // re-points it here.
        //
        // The topbar post-message dropdown that opens this page is shell
        // chrome (`features/messageboard/menu/`, the `[chat]` topbar slot —
        // ADR-0074 §3), not a route. Breadcrumb registry reads "Message
        // Board" with parent Home (`breadcrumb.js#L412-L415`).
        //
        // Unlike `/notification` this one MAY also be a sidebar item —
        // `icon-map.ts` already carries `icon-messageboard`, suggesting a
        // real menu payload uses it; the verbatim `messageboard` page_url
        // auto-resolves here, with defensive qualified label entries in
        // `menu.mapper.ts`. Confirmed at A-MB1 (KPM2-540).
        //
        // Internal folder `features/messageboard/`;
        // `MESSAGEBOARD_BASE_PATH` is the single-line rename point.
        // Migration breakup: P0 scaffold KPM2-532.
        path: 'messageboard',
        loadChildren: () =>
          import('./features/messageboard/messageboard.routes').then(
            (m) => m.MESSAGEBOARD_ROUTES,
          ),
      },
      {
        // My Profile (ADR-0054, epic KPM2-390). Legacy state `app.profile`
        // at `/profile` (`app.js#L2149-L2173`) — verbatim state URL. NOT a
        // sidebar item — the entry point is the topbar avatar menu
        // (`ProfileMenuComponent.goToProfile`), which targeted `/profile`
        // before this feature existed and wildcard-fell-through to the
        // dashboard; this registration lights the link up (work-breakdown
        // TASK-459). No menu bridge; breadcrumb "Profile" with parent Home
        // (`breadcrumb.js#L221-L223`). Internal folder `features/profile/`;
        // `PROFILE_BASE_PATH` is the single-line rename point. Migration
        // breakup: P0 scaffold KPM2-392.
        path: 'profile',
        loadChildren: () =>
          import('./features/profile/profile.routes').then(
            (m) => m.PROFILE_ROUTES,
          ),
      },
      {
        // The in-app profile editor at the legacy camelCase URL
        // (`app.profileDetails`, `app.js#L2178-L2202`; `landlordDetails`
        // camelCase precedent, ADR-0027 §3b). ORPHANED in the deployed
        // legacy UI (its `/profile` entry icon is commented out —
        // `profile.html#L11-L13`; "View / Edit Profile" opens the auth-site
        // page instead) but still routable + breadcrumb-registered there
        // ("Profile Details" under Profile, `breadcrumb.js#L226-L228`) —
        // migrated as the in-app edit surface (ADR-0054 §3). Whether it
        // gets a visible entry link is the A-PR5 sweep decision (KPM2-398).
        // Migration breakup: P2 story KPM2-396.
        path: 'profileDetails',
        loadComponent: () =>
          import(
            './features/profile/details/profile-details-page.component'
          ).then((m) => m.ProfileDetailsPageComponent),
        title: 'Profile Details',
        data: {
          breadcrumb: {
            label: 'Profile Details',
            parent: { label: 'Profile', url: '/profile' },
          },
        },
      },
      {
        // `app.invoices` at `/invoices` (`app.js#L2874-L2903`) — the invoice
        // EDITOR, both scenarios (ADR-0040 §1, epic KPM2-303). Children:
        // `new` (+ plan-§3 query params), `:id`, and a bare-path redirect to
        // `new` (legacy rendered New when its globalVar stash was empty).
        // Not a sidebar item — no menu bridge; breadcrumb parent is
        // Customer Financials (`breadcrumb.js#L250-L256`). Internal folder
        // `features/financials/invoices/` (ADR-0039 §2 family home);
        // `INVOICES_BASE_PATH` is the single-line rename point. Distinct
        // from the subscription section `/accountsbilling/invoices`
        // (KPM2-64). Migration breakup: P0 scaffold KPM2-304.
        path: 'invoices',
        loadChildren: () =>
          import('./features/financials/invoices/invoices.routes').then(
            (m) => m.INVOICES_ROUTES,
          ),
      },
      {
        // `app.payments` at `/payments` (`app.js#L2941-L2967`) — the receipt
        // EDITOR, both scenarios (ADR-0064 §1, epic KPM2-459). Children:
        // `new` (+ §3 query params `customerId`/`invoiceId`/`receiptUserType`),
        // `new/supplier` (the S seed as a clean path, ADR-0064 amendment
        // 2026-09-15), `:id`, and a bare-path redirect to `new` (legacy
        // rendered New when its `getPaymentId()` stash was empty,
        // `payments.ctrl.js#L16`). Not a sidebar item — no menu bridge;
        // breadcrumb parent is Customer Financials on the defaults, Supplier
        // Financials on `new/supplier` and by party category on `:id`
        // (`breadcrumb.js#L264-L270`; the legacy DYNAMIC parent — deviation
        // D1, ADR-0064 §5 as amended). Internal folder
        // `features/financials/payments/` (ADR-0039 §2 family home);
        // `PAYMENTS_BASE_PATH` is the single-line rename point. The SUPPLIER
        // payment sibling `/makebillpayment` (`P` rows) is the ADR-0066
        // route below. Migration breakup: P0 scaffold KPM2-460.
        path: 'payments',
        loadChildren: () =>
          import('./features/financials/payments/payments.routes').then(
            (m) => m.PAYMENTS_ROUTES,
          ),
      },
      {
        // `app.makebillpayment` at `/makebillpayment` (`app.js#L2968-L2996`)
        // — the BILL-PAYMENT editor, both scenarios (ADR-0066 §1, epic
        // KPM2-473): the `'P'`-row sibling of `/payments` above. Children
        // mirror ADR-0064 §1: `new` (+ §3 query params
        // `partyType`/`customerId`/`billId`), `:id`, and a bare-path
        // redirect to `new` (legacy rendered New when its
        // `getBillPaymentId()` stash was empty,
        // `makebillpayment.ctrl.js#L16-L20`). Not a sidebar item — no menu
        // bridge; breadcrumb parent is Supplier Financials
        // (`breadcrumb.js#L271-L277`; the legacy DYNAMIC parent is
        // deviation D1, ADR-0066 §5). Internal folder
        // `features/financials/payments/bill-editor/` (the ADR-0064 family
        // home); `MAKEBILLPAYMENT_BASE_PATH` is the single-line rename
        // point. Migration breakup: P0 scaffold KPM2-474.
        path: 'makebillpayment',
        loadChildren: () =>
          import('./features/financials/payments/makebillpayment.routes').then(
            (m) => m.MAKEBILLPAYMENT_ROUTES,
          ),
      },
      {
        // `app.clientledgertransfer` at `/clientledgertransfer`
        // (`app.js#L2997-L3025`) — the Client Ledger Transfer editor, NEW
        // scenario ONLY (ADR-0068 §1, epic KPM2-487): entry is exclusively
        // the `transferpayment` Add-New verb (both legacy dispatchers stage
        // `'New'` — no edit dispatch exists; A-CLT1). Children: `new` + a
        // bare-path redirect. Not a sidebar item — no menu bridge;
        // breadcrumb parent Customer Financials (`breadcrumb.js#L279-L284`).
        // Internal home `features/financials/payments/transfer-editor/`
        // (the ADR-0064 family folder; ADR-0066's `bill-editor/` sibling);
        // `CLIENT_LEDGER_TRANSFER_BASE_PATH` is the single-line rename
        // point. Migration breakup: P0 scaffold KPM2-488.
        path: 'clientledgertransfer',
        loadChildren: () =>
          import(
            './features/financials/payments/transfer-editor/clientledgertransfer.routes'
          ).then((m) => m.CLIENT_LEDGER_TRANSFER_ROUTES),
      },
      {
        // `app.purchaseorder` at `/purchaseorder` (`app.js#L4212-L4241`) —
        // the PURCHASE ORDER editor, both scenarios (ADR-0071 §1, epic
        // KPM2-501): the supplier-side document editor over the ADR-0063
        // shared editor layer (invoices skeleton + a PO-local payload
        // builder). Children mirror ADR-0040 §1: `new`, `:id`, and a
        // bare-path redirect to `new` (legacy rendered New when its
        // `getTransactionId()` stash was 0, `purchaseorder.ctrl.js#L20`;
        // the state carried NO url params — every inbound handoff was
        // globalVar). Not a sidebar item — no menu bridge (the singular
        // 'purchase order' label already points at the LIST, ADR-0044 §1);
        // breadcrumb parent is Purchase Orders — HARDCODED in legacy
        // (`breadcrumb.js#L299-L305`), replicated as-is (ADR-0071 §5 D1).
        // Internal home `features/financials/purchaseorders/editor/` (the
        // list feature's sibling folder); `PURCHASE_ORDER_EDITOR_BASE_PATH`
        // is the single-line rename point. Migration breakup: P0 scaffold
        // KPM2-502.
        path: 'purchaseorder',
        loadChildren: () =>
          import(
            './features/financials/purchaseorders/editor/purchase-order-editor.routes'
          ).then((m) => m.PURCHASE_ORDER_EDITOR_ROUTES),
      },
      {
        // `app.creditnotes` at `/creditnotes` (`app.js#L3056-L3084`) — the
        // credit-note EDITOR, both scenarios (ADR-0063 §1, epic KPM2-451):
        // the SECOND FLAVOR of the invoices editor above (ADR-0054
        // §promotion). Children mirror ADR-0040 §1: `new` (+ the
        // A-CRN1-pinned query params), `:id`, and a bare-path redirect to
        // `new` (legacy rendered New when its globalVar stash was empty).
        // Not a sidebar item — no menu bridge; breadcrumb parent is
        // Customer Financials (`breadcrumb.js#L286-L291`). Internal folder
        // `features/financials/creditnotes/` (ADR-0039 §2 family home);
        // `CREDIT_NOTES_BASE_PATH` is the single-line rename point.
        // Migration breakup: P0 scaffold KPM2-452.
        path: 'creditnotes',
        loadChildren: () =>
          import(
            './features/financials/creditnotes/credit-notes.routes'
          ).then((m) => m.CREDIT_NOTES_ROUTES),
      },
      {
        // `app.suppliercredit` at `/suppliercredit` (`app.js#L3081-L3110`) —
        // the supplier-credit EDITOR, both scenarios (ADR-0069 §1, epic
        // KPM2-509): the THIRD FLAVOR of the shared transaction editor
        // (`'SC'`/`'TMP-SC'`, a `bills.ctrl.js` fork over the GENERIC
        // `fetchtxntransactiondata`/`savetxntransactiondata` wire). Children
        // follow the ADR-0040/0063 route-split idiom (ADR-0066 sibling):
        // `new`, `:id`, and a bare-path redirect to `new` (legacy rendered
        // New when its `getSupplierCreditId()` globalVar stash was empty).
        // Not a sidebar item — no menu bridge; entry is drill-only
        // (host row-opens, the `suppliercredit` Add-New key, `SC` code
        // prefix, cross-rails — P4 KPM2-514). The legacy `?txnType=SC` deep
        // link landed on `app.bills` in both handler copies (`app.js
        // #L395-L401`, `login.ctrl.js#L861-L867` — copy-paste bug); 2.0
        // routes it here (recorded divergence, ADR-0069 §5 / D1). Breadcrumb
        // parent is Supplier Financials (`breadcrumb.js#L292-L298` — static
        // in legacy too, byte parity). Internal folder
        // `features/financials/suppliercredit/` (ADR-0039 §2 family home);
        // `SUPPLIER_CREDIT_BASE_PATH` is the single-line rename point.
        // Migration breakup: P0 scaffold KPM2-510.
        path: 'suppliercredit',
        loadChildren: () =>
          import(
            './features/financials/suppliercredit/supplier-credit.routes'
          ).then((m) => m.SUPPLIER_CREDIT_ROUTES),
      },
      {
        // `app.bills` at `/bills` (`app.js#L2905-L2935`) — the BILL editor,
        // both scenarios (ADR-0070 §1, epic KPM2-494): the THIRD FLAVOR of
        // the invoices editor above over the ADR-0063 §2 shared-editor seam.
        // Children mirror ADR-0040 §1: `new` (+ the A-BL1-pinned seed
        // params), `:id`, and a bare-path redirect to `new` (legacy rendered
        // New when its `getTransactionId()` stash was empty). Not a sidebar
        // item — no menu bridge (bare `bills` stays never-bridged in
        // `menu.mapper.ts`); breadcrumb parent is the synthetic Supplier
        // Financials crumb (`breadcrumb.js#L257-L263`). Home
        // `features/financials/bills/` (ADR-0039 §2 family home);
        // `BILLS_BASE_PATH` is the single-line rename point. Migration
        // breakup: P0 scaffold KPM2-495.
        path: 'bills',
        loadChildren: () =>
          import('./features/financials/bills/bills.routes').then(
            (m) => m.BILLS_ROUTES,
          ),
      },
      {
        // `app.tenantdirectpay` at `/tenantdirectpay` (`app.js#L3026`) —
        // the Tenant Direct Pay EDITOR, both scenarios (ADR-0067 §1, epic
        // KPM2-476): the dual-grid balancing sibling of the receipt editor
        // above (tenant paid a supplier directly — invoices vs bills must
        // balance). Children: `new`, `:id`, and a bare-path redirect to
        // `new` (legacy rendered New when its `getPaymentDirectId()` stash
        // was empty, `tenantpaydirect.ctrl.js#L18`). Not a sidebar item —
        // no menu bridge (reached from the finance grids' `TDP` rows and
        // `tenantpaydirect` Add-New verbs); breadcrumb parent is Customer
        // Financials (`breadcrumb.js#L435`). Internal folder
        // `features/financials/tenantdirectpay/` (ADR-0039 §2 family
        // home); `TENANTDIRECTPAY_BASE_PATH` is the single-line rename
        // point. Migration breakup: P0 scaffold KPM2-478.
        path: 'tenantdirectpay',
        loadChildren: () =>
          import(
            './features/financials/tenantdirectpay/tenantdirectpay.routes'
          ).then((m) => m.TENANTDIRECTPAY_ROUTES),
      },
      {
        // Global search (ADR-0053, epic KPM2-387). Legacy state `app.search`
        // at `/search` (`app.js#L2455-L2478`) — verbatim state URL, so the
        // header handoff and legacy bookmarks resolve unchanged and NO
        // legacy-compat redirect is needed. NOT a sidebar item — the deployed
        // entry points are the topbar box's per-group "View All" and
        // "Advanced Search" links (`templates/menu.html#L70-L305`), so there
        // is NO menu bridge either; breadcrumb reads "Search" with parent
        // Home (`breadcrumb.js#L148-L151`). Entry carries
        // `?q=…&category=…` (ADR-0053 §5a — router params replace the
        // legacy globalVar stash + `$broadcast`; note R-173a: any future
        // redirect INTO this page must copy the params explicitly). The box
        // itself is shell chrome (`features/search/box/`, mounted in the
        // shell topbar — ADR-0053 §3), not a route. Internal folder
        // `features/search/`; `SEARCH_BASE_PATH` is the single-line rename
        // point. Migration breakup: P0 scaffold KPM2-388.
        path: 'search',
        loadChildren: () =>
          import('./features/search/search.routes').then(
            (m) => m.SEARCH_ROUTES,
          ),
      },
      {
        // Consolidated Billing Customer (ADR-0055, epic KPM2-399). Legacy
        // state `app.consolidatedparties` at `/consolidatedparties`
        // (`app.js#L4966-L4991`) — verbatim state URL, so a legacy bookmark
        // resolves unchanged and a sidebar item with
        // `page_url: "consolidatedparties"` navigates here with no mapper
        // translation. The deployed item's `page_url`/label/bucket are
        // UNCAPTURED (A-CP2, live sweep KPM2-403); the label bridge in
        // `menu.mapper.ts` ships proactively (A-LLa class). The detail
        // sibling `app.partyDetails` is OUT OF SCOPE — a follow-up epic
        // (ADR-0055 §6; row drills gate per ADR-0016 §4, see the redirect
        // below). Internal folder `features/consolidatedparties/`;
        // `CONSOLIDATEDPARTIES_BASE_PATH` is the single-line rename point.
        // Migration breakup: P0 scaffold KPM2-400.
        path: 'consolidatedparties',
        loadChildren: () =>
          import(
            './features/consolidatedparties/consolidatedparties.routes'
          ).then((m) => m.CONSOLIDATEDPARTIES_ROUTES),
      },
      {
        // Consolidated Billing Customer detail (ADR-0055 addendum, P2.5 —
        // pulled into scope on user direction 2026-08-21): legacy state
        // `app.partyDetails` at `/partyDetails` (`app.js#L4992-L5023`,
        // camelCase legacy URL — the ADR-0027 §3b `landlordDetails` class).
        // The URL carries NO id — identity rides the grid-row stash
        // (`GridNavContextStore`, the `globalVar.getRowData()` analog,
        // `partyDetails.ctrl.js#L157-L163`); a stash-less entry (deep
        // link/refresh) returns to the list. The deployed surface is the
        // entity-details form + Contact grid ONLY — the Self-Billing tab and
        // supplier-invoices sections are commented OUT in frozen source.
        path: 'partyDetails',
        loadComponent: () =>
          import(
            './features/consolidatedparties/detail/party-details-page.component'
          ).then((m) => m.PartyDetailsPageComponent),
        title: 'Consolidated Billing Customer',
        data: {
          breadcrumb: {
            label: 'Details',
            parent: {
              label: 'Consolidated Billing Customer',
              url: '/consolidatedparties',
            },
          },
        },
      },
      {
        // Autoresponder Rules (ADR-0058, epic KPM2-419, P0 KPM2-421). Legacy
        // state `app.emailresponder` at `/emailresponder`
        // (`app.js#L4895-L4919`) — verbatim state URL, so a backend menu item
        // with `page_url: "emailresponder"` auto-resolves with no
        // menu-mapper translation. NO redirect sibling (single legacy URL,
        // the /tenantstatement precedent). Internal folder is
        // `features/autoresponder/` — naming describes the feature, not the
        // URL. Deployed menu identity (label/page_url/group) is uncaptured —
        // pin A-AR1 (KPM2-429).
        path: 'emailresponder',
        loadChildren: () =>
          import('./features/autoresponder/autoresponder.routes').then(
            (m) => m.AUTORESPONDER_ROUTES,
          ),
      },
      {
        // Approvals (ADR-0059, epic KPM2-422). Legacy state `app.approvals`
        // at `/approvals` (`app.js`, parent Home) — verbatim state URL, so a
        // legacy bookmark resolves unchanged and a sidebar item with
        // `page_url: "approvals"` navigates here with no mapper translation
        // (deployed capture A-AP1, KPM2-433; the label bridge in
        // `menu.mapper.ts` ships proactively, A-LLa class). A dataset-select
        // grid page: lookup header 154 (category3 'approval') drives the
        // grid identity at RUNTIME (`pg0094` + lookup-resolved grid id,
        // frozen witness `gd0105` — the purchaseorders pair, one A-AP4/A-PO1
        // capture serves both). The TENTH transactions-host flavor, mounted
        // per dataset via a config-providing outlet (ADR-0059 §2 — zero host
        // edits for the mount). Internal folder `features/approvals/`;
        // `APPROVALS_BASE_PATH` is the single-line rename point. Migration
        // breakup: P0 scaffold KPM2-428.
        path: 'approvals',
        loadChildren: () =>
          import('./features/approvals/approvals.routes').then(
            (m) => m.APPROVALS_ROUTES,
          ),
      },
      {
        // Data Grids (ADR-0060, epic KPM2-418). Legacy state `app.datagrids`
        // at `/datagrids` (`app.js`, parent Home) — verbatim state URL, so a
        // legacy bookmark resolves unchanged and a sidebar item with
        // `page_url: "datagrids"` navigates here with no mapper translation
        // (deployed capture A-DG2, KPM2-435; the label bridge in
        // `menu.mapper.ts` ships proactively, A-LLa class). The GENERAL page
        // of the lookup-154 family — a Category → Select Dataset cascade
        // whose selected row's `category` JSON carries the grid identity at
        // RUNTIME, BOTH ids (`grid_id` + `page_id` — unlike /approvals,
        // which pins pg0094). Mounted as the raw `kpm-data-grid` via a keyed
        // remount per selection (ADR-0060 §2). Migration breakup: P0
        // scaffold KPM2-420.
        path: 'datagrids',
        loadChildren: () =>
          import('./features/datagrids/datagrids.routes').then(
            (m) => m.DATAGRIDS_ROUTES,
          ),
      },
      {
        // Document Repository (ADR-0065, epic KPM2-466). Legacy state
        // `app.documentrepository` at `/documentrepository`
        // (`app.js#L2508-L2539`, parent Home) — verbatim state URL, so a
        // legacy bookmark resolves unchanged and a sidebar item with
        // `page_url: "documentrepository"` (menu registry item mn0036,
        // `controller.js#L786`) navigates here with no mapper translation
        // (deployed capture A-DR3, KPM2-472; the label bridge in
        // `menu.mapper.ts` ships proactively, A-LLa class). NOT to be
        // confused with the bare `/document` deeplink redirect below
        // (ADR-0016 — a DIFFERENT legacy surface) or the settings tab at
        // `/customsettings/document-repository` (pg0034/gd0047). One page,
        // one pinned page id (pg0063), three grids behind the
        // Linked / Unlinked / Deleted Items toggle (ADR-0065 §2).
        // Migration breakup: P0 scaffold KPM2-467.
        path: 'documentrepository',
        loadChildren: () =>
          import(
            './features/documentrepository/documentrepository.routes'
          ).then((m) => m.DOCUMENTREPOSITORY_ROUTES),
      },
      {
        // Reports catalogue (ADR-0049 §1 amendment, KPM2-546). Legacy state
        // `app.reports` at `/reports` (`app.js#L3111-L3132`, parent Home) —
        // verbatim state URL, so a legacy bookmark resolves unchanged and a
        // sidebar item carrying `page_url: "reports"` navigates here with no
        // mapper translation and no redirect.
        //
        // This route is the BINDING half of the module only. ADR-0049 §1
        // had recorded that the bare label 'reports' is a generic
        // group-header word and must never be bridged; that was half right.
        // The deployed sidebar word may well head a group, but legacy also
        // has a real leaf page here, so the label bridge in `menu.mapper.ts`
        // now binds bare 'reports' ONLY for a childless item — a genuine
        // group header (children.length > 0) is still never hijacked.
        //
        // The second legacy state, `app.reportviewer` at `/reportviewer`
        // (`app.js#L3134-L3157`) — the 875-line report runner behind the
        // five cards — is NOT migrated here and carries its own epic. Until
        // it lands, each card's Run action is a `gateMessage()` nav-gate.
        // Deliberately NO `/reportviewer` redirect ships yet: pointing it at
        // the catalogue would silently swallow a legacy bookmark that used
        // to run a report. Deployed capture rides A-RP1..A-RP4 (KPM2-546).
        path: 'reports',
        loadChildren: () =>
          import('./features/reports/reports.routes').then(
            (m) => m.REPORTS_ROUTES,
          ),
      },
      {
        // Legacy compatibility (ADR-0022): the old app's property read-view
        // deeplink `/propertyview/?property=<id>` (`app.js#L2154`). The id is
        // the raw PK — it passes straight into the canonical `/properties/:id`.
        path: 'propertyview',
        pathMatch: 'full',
        redirectTo: (redirect) => {
          const property = redirect.queryParams['property'];
          return typeof property === 'string' && property !== ''
            ? `properties/${property}`
            : 'properties';
        },
      },
      {
        // Legacy compatibility (ADR-0022): `/propertydetails/?property=<id>`
        // (`app.js#L1436`) was the add/edit form — `property=New` meant create.
        path: 'propertydetails',
        pathMatch: 'full',
        redirectTo: (redirect) => {
          const property = redirect.queryParams['property'];
          if (typeof property !== 'string' || property === '') {
            return 'properties';
          }
          return property === 'New'
            ? 'properties/new'
            : `properties/${property}/edit`;
        },
      },
      // Legacy compatibility (ADR-0022): these drill-down states carried no
      // usable id in the URL (`globalVar` ferried the context —
      // `app.js#L1481-L1646`), so a stale bookmark can only land on the list.
      ...['location', 'viewunitdetails', 'contentdetails', 'addlocation',
          'propertyreadiness', 'propertyinfo'].map((legacyPath) => ({
        path: legacyPath,
        pathMatch: 'full' as const,
        redirectTo: 'properties',
      })),
      {
        // Same verbatim-page_url rule as the routes above: the legacy grid
        // state is `app.esignatures` at `/esignatures` (`app.js#L3157-L3181`),
        // so this is the URL. Internal folder is `features/esignature/`.
        // Migration breakup: epic KPM2-189, shell ticket KPM2-191. The detail
        // editor lives under `/esignatures/:envelopeId` + `/esignatures/new`
        // (ADR-0019).
        path: 'esignatures',
        loadChildren: () =>
          import('./features/esignature/esignature.routes').then(
            (m) => m.ESIGNATURE_ROUTES,
          ),
      },
      {
        // Legacy compatibility (ADR-0019): the old app had a top-level
        // `/esignature?id=<envelopeId>&name=<envelopeName>` deeplink
        // (singular — `app.js#L375-L383`, post-login `login.ctrl.js#L616-L624`)
        // that stashed the id in `$sessionStorage` and `$state.go`'d to the
        // editor. Canonicalise it to `/esignatures/:envelopeId`; the router
        // preserves the query params so `name` rides along as the breadcrumb
        // seed until the fetched `envelope_code` supersedes it. Without an
        // `id` it lands on the envelope list. Lives here (not in
        // `esignature.routes.ts`) because the legacy URL is top-level
        // (`/esignature`, singular), not under `/esignatures` — same
        // placement rule as the `/case` redirect below.
        path: 'esignature',
        pathMatch: 'full',
        redirectTo: (redirect) => {
          const id = redirect.queryParams['id'];
          return typeof id === 'string' && id !== ''
            ? `esignatures/${id}`
            : 'esignatures';
        },
      },
      {
        // Legacy compatibility (ADR-0016): the old app's document deeplink
        // `/document?context=property&id1=<propertyId>&id1_name=<name>&id2=<ctx>`
        // (`app.js#L276-L286`) landed on the property read view. Canonicalise
        // the property context to `/properties/:id`; the router preserves the
        // query params (`context`/`id2`/`id1_name`) across the redirect for
        // the future detail surface. The landlord context (`app.js#L293-L298`,
        // legacy target: the un-migrated landlordDetails editor) lands on the
        // `/landlord` list with its params riding along (ADR-0027 §3c —
        // supersedes the ADR-0016 fall-through for this context; upgrades to
        // `/landlord/:id` when the details epic lands). The supplier context
        // (`app.js#L299-306`, legacy target: `app.supplierDetails` via
        // `globalVar.setSupplierId($location.search().id1)`) canonicalises to
        // `/supplier/:id1` (ADR-0031 §3c — supersedes the ADR-0016 fall-through
        // for this context). The tenancy context (`app.js#L287-292`)
        // canonicalises to `/tenancy/:id1` (ADR-0032 §2 — supersedes the
        // ADR-0016 "stays unhandled" note now that the tenancy detail route
        // exists; degrades to the list without an id).
        path: 'document',
        pathMatch: 'full',
        redirectTo: (redirect) => {
          const context = redirect.queryParams['context'];
          const id1 = redirect.queryParams['id1'];
          if (context === 'property' && typeof id1 === 'string' && id1 !== '') {
            return `properties/${id1}`;
          }
          if (context === 'landlord') {
            return 'landlord';
          }
          if (context === 'supplier') {
            // Legacy deeplink always carries id1; degrade to the list (not the
            // dashboard) if it is missing, matching the landlord disposition.
            return typeof id1 === 'string' && id1 !== ''
              ? `supplier/${id1}`
              : 'supplier';
          }
          if (context === 'tenancy') {
            // ADR-0032 §2: the tenancy detail route now exists; canonicalise the
            // legacy tenant-side document deeplink to `/tenancy/:id1`, degrading
            // to the list when the id is absent (landlord/supplier disposition).
            return typeof id1 === 'string' && id1 !== ''
              ? `tenancy/${id1}`
              : 'tenancy';
          }
          return 'dashboard';
        },
      },
      {
        // Legacy compatibility: the old app had a top-level `/case?id=<id>`
        // case-detail deeplink (`app.js#L97`, `app.casereview`). Canonicalise
        // it to `/cases/:caseNumber`. The `id` carries the raw primary key;
        // Phase 3's resolver (ADR-Cyy) normalises a number or a primary key.
        // Lives here (not in `cases.routes.ts`) because the legacy URL is
        // top-level (`/case`, singular), not under `/cases`.
        path: 'case',
        pathMatch: 'full',
        redirectTo: (redirect) => {
          const id = redirect.queryParams['id'];
          return typeof id === 'string' && id !== '' ? `cases/${id}` : 'cases';
        },
      },
    ],
  },
  { path: '**', redirectTo: 'dashboard' },
];
